import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  OptionsList as StyledOptionsList,
  Option as StyledOption,
  Title,
  Text,
  SubText,
  Checkbox,
  SkeletonWrapper,
  NotFoundWrapper,
  SearchWrapper,
  SkeletonContainer,
} from "./index.styled";
import { ReactComponent as SortByIcon } from "../../Assets/Icons/SortByIcon.svg";
import { useQuery } from "@tanstack/react-query";
import { searchHospitals } from "../../services/apiService";
import { TextSkeleton } from "../Skeletons/index.styled";
import HospitalComparisonSearch from "../HospitalComparisonSearch";
import { formatLocation, toTitleCase } from "../../utils";
import { LoadMoreContainer } from "../CustomTable/index.styled";
import TableLoader from "./../CustomTable/Loader";
import Info from "../Info";

const HospitalSearchList = ({
  ref,
  options,
  onOptionClick,
  selectedOption,
  customWidth,
  customPadding,
  customBorderRadius,
  top,
  right,
  showTitle,
  isOpen,
  showIcon,
  showCheckbox,
  height,
  showSearch = false,
  searchTerm,
  setSearchTerm,
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef(null);
  const [visibleData, setVisibleData] = useState(options?.slice(0, 100));
  const [hasMore, setHasMore] = useState(options.length > 100);
  const [debouncedTerm, setDebouncedTerm] = useState("");

  useEffect(() => {
    if (searchTerm.trim() !== "" || !hasMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setTimeout(() => loadMoreData(), 500);
      }
    });

    const interval = setInterval(() => {
      const element = document.getElementById("loadMoreTrigger");
      if (element) {
        observer.observe(element);
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [visibleData, searchTerm, hasMore]);

  const loadMoreData = () => {
    setVisibleData((prevData) => {
      const newData = options?.slice(0, prevData.length + 100);
      setHasMore(newData.length < options.length);
      return newData;
    });
  };

  useEffect(() => {
    setVisibleData(options?.slice(0, 100));
    setHasMore(options.length > 100);
  }, [options]);

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
      setSearchTerm("");
    }
  }, [isOpen]);

  // Debounce the raw search term before it drives the query.
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim());
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // Hospital search is cached per term; React Query cancels stale in-flight
  // requests via the forwarded signal (replacing the manual AbortController).
  const { data: searchResponse, isFetching } = useQuery({
    queryKey: ["hospital-search", debouncedTerm],
    queryFn: ({ signal }) => searchHospitals(debouncedTerm, signal),
    enabled: debouncedTerm.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const trimmedTerm = searchTerm.trim();
  // Loader while the debounce settles or while the request is in flight.
  const loading =
    trimmedTerm !== "" && (debouncedTerm !== trimmedTerm || isFetching);

  const filteredOptions = useMemo(() => {
    if (trimmedTerm === "") return options;
    const results = searchResponse?.data || [];
    const mapped = results.map((hospital) => ({
      label: toTitleCase(hospital.hosp10_name),
      location: formatLocation(`${hospital.city}, ${hospital.state}`),
      provider_number: hospital.provider_number,
      value: hospital.hosp10_name,
    }));
    return mapped.filter(
      (option) => option.provider_number !== selectedOption?.provider_number,
    );
  }, [trimmedTerm, searchResponse, options, selectedOption]);

  const handleKeyDown = (e) => {
    if (!isOpen || filteredOptions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) => {
        const nextIndex =
          prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : 0;
        listRef.current?.children[nextIndex]?.scrollIntoView({
          block: "nearest",
        });
        if (prevIndex === filteredOptions.length - 1 && nextIndex === 0) {
          if (ref.current) {
            const list = ref.current;
            list.scrollTop = -40;
          }
        }
        return nextIndex;
      });
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) => {
        const nextIndex =
          prevIndex > 0 ? prevIndex - 1 : filteredOptions.length - 1;
        listRef.current?.children[nextIndex]?.scrollIntoView({
          block: "nearest",
        });
        return nextIndex;
      });
    }
    if (e.key === "Enter" && highlightedIndex !== -1) {
      onOptionClick(filteredOptions[highlightedIndex]);
    }
  };

  const handleSortClick = (e) => {
    e.stopPropagation();
  };

  return (
    <StyledOptionsList
      ref={ref ? ref : null}
      customWidth={customWidth}
      customPadding={customPadding}
      top={top}
      right={right}
      isOpen={isOpen}
      height={height}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {showTitle && (
        <Title onClick={handleSortClick}>
          <Text>{showTitle}</Text>
          {showIcon && <SortByIcon />}
        </Title>
      )}
      {showSearch && (
        <SearchWrapper>
          <HospitalComparisonSearch
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        </SearchWrapper>
      )}
      {!loading && Object.keys(selectedOption).length !== 0 &&
        <Info
          showArrow={false}
          config={{
            tooltipText: "Currently selected hospital",
            position: "top",
          }}
        >
          <StyledOption
            customBorderRadius={customBorderRadius}
            isHighlighted={true}
            disabled={true}
            >
            <div>
              {selectedOption.label}
              {selectedOption.location && <SubText>{selectedOption.location}</SubText>}
            </div>
          </StyledOption>
        </Info>
      }
      {loading ? (
        <SkeletonContainer>
          {[...Array(6)].map((_, i) => (
            <SkeletonWrapper>
              <TextSkeleton key={i} height={2} width={18} borderRadius={0.5} />
              <TextSkeleton key={i} height={1} width={10} borderRadius={0.35} />
            </SkeletonWrapper>
          ))}
        </SkeletonContainer>
      ) : searchTerm.trim() !== "" && filteredOptions.length === 0 ? (
        <NotFoundWrapper>
          <Text>No results found...</Text>
        </NotFoundWrapper>
      ) : (
        <div ref={listRef}>
          {(searchTerm.trim() !== "" ? filteredOptions : visibleData).map(
            (option, index) => (
              <StyledOption
                key={
                  option.provider_number
                    ? `${option.provider_number}`
                    : option.value
                }
                onClick={(e) => {
                  onOptionClick(option);
                  e.stopPropagation();
                }}
                isSelected={
                  showCheckbox
                    ? selectedOption?.includes(option.value)
                    : selectedOption === option.value
                }
                customBorderRadius={customBorderRadius}
                isHighlighted={
                  option.provider_number === selectedOption.provider_number
                    ? true
                    : index === highlightedIndex
                }
              >
                <div>
                  {option.label}
                  {option.location && <SubText>{option.location}</SubText>}
                </div>
                {showCheckbox && (
                  <Checkbox
                    type="checkbox"
                    checked={
                      showCheckbox && selectedOption?.includes(option.value)
                    }
                    onChange={() => onOptionClick(option)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </StyledOption>
            ),
          )}
          {hasMore && searchTerm.trim() === "" && (
            <LoadMoreContainer id="loadMoreTrigger">
              <TableLoader />
            </LoadMoreContainer>
          )}
        </div>
      )}
    </StyledOptionsList>
  );
};

export default HospitalSearchList;
